import type {
  ActionFactory,
  EditActionDefinition,
  EditActionsDictionary,
  HostEditActionDefinition,
  HostEditAction,
  ActionNames,
  NativeActionContext,
  PlatformApiMethods,
  ActionUiCustomization,
  AiMetadata,
} from '@wix/editor-platform-host-integration-apis';
import { Context } from '@wix/editor-platform-interfaces';
import { Action, DashboardAction } from '@wix/component-protocol/schema';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import { ActionContext } from './ActionContext';

type ManifestActionWithExecution<TAction extends Action> = TAction & {
  displayName: NonNullable<TAction['displayName']>;
  execution: NonNullable<TAction['execution']>;
};

type CustomizedNativeManifestAction = ManifestActionWithExecution<Action>;

type CustomizedDashboardManifestAction = Omit<
  ManifestActionWithExecution<DashboardAction>,
  'dashboardPageId'
> & { dashboardPageId?: string };

type CustomManifestAction = ManifestActionWithExecution<Action>;

type CustomEditActionsDictionary<TAction extends Action> = Record<
  string,
  TAction
>;

const isCustomManifestAction = (
  action: CustomManifestAction | Action,
): action is CustomManifestAction => {
  return 'execution' in action;
};

type PlatformContext = Context<ComponentsPlatformService, PlatformApiMethods>;

type ExtendedPlatformContext = PlatformContext &
  Omit<NativeActionContext, 'contextAPI'>;

type ActionFactoryWithPlatformContext = ActionFactory<
  ExtendedPlatformContext,
  EditActionDefinition | HostEditActionDefinition
>;

type EditAction = HostEditAction | ActionFactoryWithPlatformContext;

type ContributedPlatformActionsDict =
  EditActionsDictionary<ActionFactoryWithPlatformContext>;

type ContributedEditActionsDict = EditActionsDictionary<EditAction>;

type ActionUI = {
  displayName?: string;
  uiCustomization?: ActionUiCustomization;
  aiMetadata?: AiMetadata;
};

type CustomizedManifestAction =
  | CustomizedNativeManifestAction
  | CustomizedDashboardManifestAction;

abstract class IEditActionSource<TActionName extends string = string> {
  constructor(protected readonly ctx: PlatformContext) {}

  abstract getDefinitions(): Partial<Record<TActionName, Action>>;
}

abstract class IEditActionBuilder<TActionName extends string = string> {
  constructor(protected readonly ctx: ActionContext) {}

  abstract build(
    actionName: TActionName,
    actionDefinition: Action,
  ): EditActionDefinition<TActionName> | null;
}

export {
  CustomizedNativeManifestAction,
  CustomizedDashboardManifestAction,
  CustomizedManifestAction,
  CustomManifestAction,
  ActionNames,
  EditActionsDictionary,
  CustomEditActionsDictionary,
  PlatformContext,
  ExtendedPlatformContext,
  ActionFactoryWithPlatformContext,
  EditAction,
  ContributedPlatformActionsDict,
  ContributedEditActionsDict,
  isCustomManifestAction,
  ActionUI,
  IEditActionSource,
  IEditActionBuilder,
};
